import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import SEO from '../components/SEO';
import { 
  ClipboardCheck, 
  ArrowRight, 
  CheckCircle,
  Building,
  Package,
  Globe,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { id: 'manufacturer', label: 'Manufacturer' },
  { id: 'brand_owner', label: 'Brand owner' },
  { id: 'importer', label: 'Importer' },
  { id: 'distributor', label: 'Distributor' },
  { id: 'distance_seller', label: 'Distance seller / Online seller' },
  { id: 'other_role', label: 'Other' },
];

// Plastic packaging only for AfriCycle Zambia
const PRODUCT_CATEGORIES = [
  { id: 'plastic_packaging', label: 'Plastic packaging (general)' },
  { id: 'pet_packaging', label: 'PET plastic packaging' },
  { id: 'hdpe_packaging', label: 'HDPE plastic packaging' },
  { id: 'ldpe_packaging', label: 'LDPE plastic packaging' },
  { id: 'pp_packaging', label: 'PP plastic packaging' },
  { id: 'other_plastic', label: 'Other plastic packaging' },
];

// WEEE categories (EU WEEE Directive categories)
const WEEE_CATEGORIES = [
  { id: 'weee_temp_exchange', label: 'Temperature Exchange Equipment (e.g., refrigerators, air conditioners)' },
  { id: 'weee_screens', label: 'Screens and Monitors (e.g., TVs, monitors, laptops)' },
  { id: 'weee_lamps', label: 'Lamps (e.g., fluorescent lamps, LED lamps)' },
  { id: 'weee_large', label: 'Large Equipment (e.g., washing machines, dishwashers)' },
  { id: 'weee_small', label: 'Small Equipment (e.g., vacuum cleaners, microwaves, toasters)' },
  { id: 'weee_it_telecom', label: 'IT and Telecommunication Equipment (e.g., computers, printers, phones)' },
];

// Zambia only market
const MARKETS = [
  { id: 'zambia', label: 'Zambia' },
];

export default function SelfAssessment() {
  const [formData, setFormData] = useState({
    roles: [],
    otherRole: '',
    products: [],
    otherProduct: '',
    weee: [],
    otherWeee: '',
    markets: ['zambia'], // Pre-select Zambia
    otherMarket: '',
    companyName: '',
    email: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCheckboxChange = (field, id, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked 
        ? [...prev[field], id]
        : prev[field].filter(item => item !== id)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (formData.roles.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    if (formData.products.length === 0 && formData.weee.length === 0) {
      toast.error('Please select at least one product or WEEE category');
      return;
    }

    setLoading(true);
    
    try {
      // Send assessment data to backend for email forwarding
      const response = await fetch(`${API_BASE}/api/self-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roles: formData.roles,
          otherRole: formData.otherRole,
          plasticCategories: formData.products,
          otherPlastic: formData.otherProduct,
          weeeCategories: formData.weee,
          otherWeee: formData.otherWeee,
          companyName: formData.companyName,
          email: formData.email
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit assessment');
      }
      
      setSubmitted(true);
      toast.success('Assessment submitted successfully!');
    } catch (error) {
      console.error('Error submitting assessment:', error);
      // Still show result even if email fails
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]" data-testid="assessment-result">
        {/* Header */}
        <section className="bg-white py-12 border-b border-gray-100">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D2E4D6] text-[#056839] mb-4">
              <CheckCircle className="h-8 w-8" />
            </div>
            <h1 className="text-3xl font-bold text-[#056839] mb-4">Assessment Complete</h1>
          </div>
        </section>

        {/* Result */}
        <section className="py-12">
          <div className="max-w-2xl mx-auto px-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center">
              <div className="bg-[#D2E4D6]/30 rounded-lg p-6 mb-6">
                <p className="text-lg text-[#3F3F3F] leading-relaxed">
                  Based on your answers, <strong className="text-[#056839]">you might have EPR obligations</strong> for {formData.products.length > 0 && formData.weee.length > 0 ? 'plastic packaging and WEEE' : formData.products.length > 0 ? 'plastic packaging' : 'WEEE'} in Zambia. Please review our services to ensure compliance.
                </p>
              </div>
              
              <Link to="/services">
                <Button 
                  className="bg-[#056839] text-white hover:bg-[#04522d] rounded-full px-8 py-3 font-medium text-lg"
                  data-testid="view-services-result-btn"
                >
                  View Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>

              <p className="text-sm text-[#6B7280] mt-6">
                Need personalized guidance?{' '}
                <Link to="/contact" className="text-[#056839] hover:underline">Contact us</Link>
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="self-assessment-page">
      <SEO 
        title="EPR Self-Assessment Tool | AfriCycle Zambia Compliance Check"
        description="Use AfriCycle Zambia's free self-assessment tool to determine your EPR compliance obligations for plastic packaging and WEEE in Zambia. Check if you need to register now."
        keywords="EPR self-assessment Zambia, plastic packaging compliance check, WEEE obligations test, producer responsibility assessment, AfriCycle compliance tool"
        canonicalUrl="https://africycle.pro/self-assessment"
      />
      {/* Header */}
      <section className="bg-white py-12 border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#D2E4D6] text-[#056839] mb-4">
            <ClipboardCheck className="h-7 w-7" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#056839] mb-4">
            Self-Assessment: Do I have EPR obligations?
          </h1>
          <p className="text-lg text-[#3F3F3F]">
            Answer a few questions to understand if EPR for plastic packaging or WEEE (e-waste) may apply to your business in Zambia.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Question 1: Role */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="role-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#056839] text-white flex items-center justify-center text-sm font-semibold">1</div>
                <div className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-[#056839]" />
                  <h2 className="text-lg font-semibold text-[#056839]">Your role in the supply chain</h2>
                </div>
              </div>
              <p className="text-sm text-[#6B7280] mb-4">Select all that apply <span className="text-red-500">*</span></p>
              
              <div className="space-y-3">
                {ROLES.map(role => (
                  <div key={role.id} className="flex items-center space-x-3">
                    <Checkbox 
                      id={role.id}
                      checked={formData.roles.includes(role.id)}
                      onCheckedChange={(checked) => handleCheckboxChange('roles', role.id, checked)}
                      data-testid={`role-${role.id}`}
                    />
                    <Label htmlFor={role.id} className="cursor-pointer text-[#3F3F3F]">{role.label}</Label>
                  </div>
                ))}
              </div>
              
              {formData.roles.includes('other_role') && (
                <div className="mt-4">
                  <Input
                    placeholder="Please specify your role"
                    value={formData.otherRole}
                    onChange={(e) => setFormData({ ...formData, otherRole: e.target.value })}
                    className="max-w-md"
                    data-testid="other-role-input"
                  />
                </div>
              )}
            </div>

            {/* Question 2a: Product Categories - Plastic Packaging Only */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="product-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#056839] text-white flex items-center justify-center text-sm font-semibold">2a</div>
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#056839]" />
                  <h2 className="text-lg font-semibold text-[#056839]">Plastic packaging categories</h2>
                </div>
              </div>
              <p className="text-sm text-[#6B7280] mb-4">Select all that apply <span className="text-red-500">*</span></p>
              
              <div className="space-y-3">
                {PRODUCT_CATEGORIES.map(product => (
                  <div key={product.id} className="flex items-center space-x-3">
                    <Checkbox 
                      id={product.id}
                      checked={formData.products.includes(product.id)}
                      onCheckedChange={(checked) => handleCheckboxChange('products', product.id, checked)}
                      data-testid={`product-${product.id}`}
                    />
                    <Label htmlFor={product.id} className="cursor-pointer text-[#3F3F3F]">{product.label}</Label>
                  </div>
                ))}
              </div>
              
              {formData.products.includes('other_plastic') && (
                <div className="mt-4">
                  <Input
                    placeholder="Please specify the plastic packaging type"
                    value={formData.otherProduct}
                    onChange={(e) => setFormData({ ...formData, otherProduct: e.target.value })}
                    className="max-w-md"
                    data-testid="other-product-input"
                  />
                </div>
              )}
            </div>

            {/* Question 2b: WEEE Categories */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="weee-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#056839] text-white flex items-center justify-center text-sm font-semibold">2b</div>
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-[#056839]" />
                  <h2 className="text-lg font-semibold text-[#056839]">WEEE (Electrical & Electronic Equipment)</h2>
                </div>
              </div>
              <p className="text-sm text-[#6B7280] mb-4">Select all that apply (if applicable)</p>
              
              <div className="space-y-3">
                {WEEE_CATEGORIES.map(weee => (
                  <div key={weee.id} className="flex items-center space-x-3">
                    <Checkbox 
                      id={weee.id}
                      checked={formData.weee.includes(weee.id)}
                      onCheckedChange={(checked) => handleCheckboxChange('weee', weee.id, checked)}
                      data-testid={`weee-${weee.id}`}
                    />
                    <Label htmlFor={weee.id} className="cursor-pointer text-[#3F3F3F]">{weee.label}</Label>
                  </div>
                ))}
              </div>
              
              {formData.weee.includes('weee_other') && (
                <div className="mt-4">
                  <Input
                    placeholder="Please specify the WEEE type"
                    value={formData.otherWeee}
                    onChange={(e) => setFormData({ ...formData, otherWeee: e.target.value })}
                    className="max-w-md"
                    data-testid="other-weee-input"
                  />
                </div>
              )}
            </div>

            {/* Question 3: Market - Zambia Only */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="market-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#056839] text-white flex items-center justify-center text-sm font-semibold">3</div>
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-[#056839]" />
                  <h2 className="text-lg font-semibold text-[#056839]">Market</h2>
                </div>
              </div>
              <p className="text-sm text-[#6B7280] mb-4">AfriCycle Zambia operates exclusively in Zambia</p>
              
              <div className="p-4 bg-[#D2E4D6]/30 rounded-lg border border-[#D2E4D6]">
                <div className="flex items-center space-x-3">
                  <Checkbox 
                    id="zambia"
                    checked={true}
                    disabled={true}
                    data-testid="market-zambia"
                  />
                  <Label htmlFor="zambia" className="cursor-pointer text-[#056839] font-medium">Zambia</Label>
                </div>
              </div>
            </div>

            {/* Optional: Company Info */}
            <div className="bg-[#E4D5B7]/20 rounded-xl border border-[#E4D5B7] p-6" data-testid="optional-section">
              <h2 className="text-lg font-semibold text-[#056839] mb-2">Optional Information</h2>
              <p className="text-sm text-[#6B7280] mb-4">Not required to complete the assessment</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#3F3F3F]">Company Name</Label>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    placeholder="Your company name"
                    className="mt-1"
                    data-testid="company-name-input"
                  />
                </div>
                <div>
                  <Label className="text-[#3F3F3F]">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@company.com"
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="text-center">
              <Button 
                type="submit"
                disabled={loading}
                className="bg-[#056839] text-white hover:bg-[#04522d] rounded-full px-10 py-4 font-medium text-lg disabled:opacity-50"
                data-testid="submit-assessment-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Assessment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
